import React, { useRef, useState } from "react";
import { ClickAwayListener, IconButton, Paper, Popper, Typography } from "@mui/material";
import { InfoOutlined } from "@mui/icons-material";
import { getTranslation } from "common";
import { useTranslations } from "hooks";

interface UserDataFieldsInfoProps {
  /**
   * Optional className / sx passthrough for the trigger button,
   * useful to align size/position in different layouts (inline in
   * AccordionSummary, next to a section title, etc.).
   */
  triggerSx?: React.ComponentProps<typeof IconButton>["sx"];
  /**
   * Stops click propagation. Set to `true` when the trigger is
   * rendered inside an interactive parent (e.g. AccordionSummary)
   * to avoid toggling the parent on info-click.
   */
  stopPropagation?: boolean;
}

const UserDataFieldsInfo: React.FC<UserDataFieldsInfoProps> = ({
  triggerSx,
  stopPropagation = false,
}) => {
  const translations = useTranslations();
  const [open, setOpen] = useState<boolean>(false);
  const anchorRef = useRef<HTMLButtonElement | null>(null);

  const titleText =
    getTranslation(translations, "userdataModelFieldsInfo") ||
    "Informazioni sui campi del modello";
  const bodyText =
    getTranslation(translations, "userdataModelFieldsInfoBody") ||
    "I campi del modello sono definiti nello schema del processo. Puoi compilarli, modificarli o aggiungerne di nuovi durante l'esecuzione.";

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (stopPropagation) e.stopPropagation();
    setOpen((v) => !v);
  };

  return (
    <>
      <IconButton
        ref={anchorRef}
        size="small"
        onClick={handleClick}
        aria-label={titleText}
        aria-haspopup="dialog"
        aria-expanded={open}
        sx={{
          p: 0.25,
          ml: 1,
          color: "text.secondary",
          "&:hover": { color: "primary.main", bgcolor: "transparent" },
          ...triggerSx,
        }}
      >
        <InfoOutlined sx={{ fontSize: 18 }} />
      </IconButton>
      <Popper
        open={open}
        anchorEl={anchorRef.current}
        placement="bottom-start"
        disablePortal={false}
        modifiers={[{ name: "offset", options: { offset: [0, 6] } }]}
        sx={{ zIndex: (theme) => theme.zIndex.modal }}
      >
        <ClickAwayListener onClickAway={() => setOpen(false)}>
          <Paper
            elevation={6}
            sx={{
              p: 1.5,
              maxWidth: 320,
              borderRadius: 1.5,
              border: "1px solid",
              borderColor: "divider",
              borderLeft: "3px solid",
              borderLeftColor: "primary.main",
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
              {titleText}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {bodyText}
            </Typography>
          </Paper>
        </ClickAwayListener>
      </Popper>
    </>
  );
};

export default UserDataFieldsInfo;
